<template>
  <div class="pa-3">
    <v-row>
      <template v-for="sector in model.mapping?.sectors?.sort((a, b) => a.order - b.order).filter(s => s.active)" :key="sector.id">
        <v-col v-for="section in sector.sections.filter(s => s.active)" :key="section.id" cols="12">
          <v-card elevation="3">
            <template #title>
              <span>{{ sector.name }}</span>
            </template>
            <template #subtitle>
              <span>{{ section.name }}</span>
            </template>
            <template #append>
              <v-btn
                v-if="section.ticket_types?.length"
                color="primary"
                :loading="loader.loading"
                :disabled="loader.loading"
                @click="sectionOnSorting = section"
              >
                Ordenar Tipos
              </v-btn>
            </template>

            <v-card-text>
              <v-table>
                <thead>
                  <tr>
                    <th class="text-left">
                      Tipo
                    </th>
                    <th class="text-left">
                      Preço
                    </th>
                    <th class="text-left">
                      <div class="d-flex ga-2">
                        <span>Quantidade</span>
                        <t-tooltip>
                          Quantidade de ingressos gerados na compra de um item
                        </t-tooltip>
                      </div>
                    </th>
                    <th class="text-left">
                      Max. Parcelas
                    </th>
                    <th class="text-left">
                      Max. Por Comprador
                    </th>
                    <th class="text-left">
                      Observações
                    </th>
                    <th class="text-center">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="type in section.ticket_types" :key="type.id">
                    <tr>
                      <td>{{ type.name }}</td>
                      <td>{{ currency(type.price / 100) }}</td>
                      <td>{{ type.passport_amount }}</td>
                      <td>{{ type.max_installments }}</td>
                      <td>{{ type.max_per_user ?? 'Ilimitado' }}</td>
                      <td>{{ type.observations }}</td>
                      <td class="text-center">
                        <v-btn
                          icon
                          class="mr-2 pa-2"
                          density="compact"
                          min-height="32"
                          min-width="32"
                          elevation="1"
                          @click="openForm(sector, section, type)"
                        >
                          <v-icon color="info" icon="fa-solid fa-pen" size="x-small" />
                        </v-btn>
                        <v-btn
                          icon
                          class="mr-2 pa-2"
                          density="compact"
                          min-height="32"
                          min-width="32"
                          elevation="1"
                          :disabled="FreeTypes.includes(type.name)"
                          @click="destroy(type, section)"
                        >
                          <v-icon :color="FreeTypes.includes(type.name) ? 'gray' : 'error'" icon="fa-solid fa-trash" size="x-small" />
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tfoot>
                  <tr v-if="copy && copy.section_id === section.id">
                    <td colspan="42" class="pa-3">
                      <form-ticket-type
                        v-model="copy"
                        :editing="!!copy.id"
                        @cancel="closeForm"
                        @submit="saveChanges"
                      />
                    </td>
                  </tr>
                  <tr v-if="!(copy && copy.section_id === section.id)">
                    <td colspan="42" style="border: none">
                      <div class="d-flex justify-center">
                        <v-btn
                          color="primary"
                          icon="fa-solid fa-plus"
                          size="x-small"
                          @click="openForm(sector, section)"
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </v-table>
            </v-card-text>
          </v-card>
        </v-col>
      </template>
    </v-row>
    <dialog-ticket-types-sorting
      v-if="sectionOnSorting"
      v-model:section="sectionOnSorting"
      :configuration-id="model.id"
      @close="sectionOnSorting = undefined"
    />
  </div>
</template>

<script lang="ts" setup>
import { currency } from '@/helpers/formatters/currency'
import EventConfiguration from '@/types/EventConfiguration'
import { FreeTypes } from '@/constants/TicketTypeNames'
import TicketType from '@/types/TicketType'
import { computed, onMounted, ref } from 'vue'
import AdditionalField from '@/types/AdditionalField'
import Section from '@/types/Section'
import FormTicketType from './form-ticket-type.vue'
import Sector from '@/types/Sector'
import TicketTypeService from '@/services/TicketTypeService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import { useDialog } from '@/stores/dialog'
import TTooltip from '@/components/TTooltip.vue'
import DialogTicketTypesSorting from './dialog-ticket-types-sorting.vue'

const loader = useLoader()
const snackbar = useSnackBar()
const dialog = useDialog()

const ticketService = new TicketTypeService()

const model = defineModel<EventConfiguration>({ required: true })

defineProps<{ editing?: boolean }>()

const sections = computed(() => model.value.mapping?.sectors?.flatMap(s => s.sections) ?? [])

const newTypes = ref<TicketType[]>([])

const copy = ref<TicketType>()

const original = ref<TicketType>()

function openForm(sector: Sector, section: Section, originalType?: TicketType) {
  const targetType = originalType
    ? { ...originalType }
    : {
        name: '',
        price: 0,
        passport_amount: 1,
        max_installments: 1,
        section_capacity: sector.capacity, // lol
        section_id: section.id,
        section_name: section.name,
        sector_id: sector.id,
        sector_name: sector.name,
        additional_fields: [] as AdditionalField[],
      } as TicketType

  copy.value = targetType
  original.value = originalType
}

function closeForm() {
  copy.value = undefined
  original.value = undefined
}

function saveChanges() {
  const section = sections.value.find(s => s.id === copy.value?.section_id)

  if (!section) return

  section.ticket_types = section.ticket_types ?? []

  if (original.value) {
    section.ticket_types = section.ticket_types.map(t => t === original.value ? copy.value! : t)
  } else {
    section.ticket_types.push(copy.value!)
    newTypes.value.push(copy.value!)
  }
  closeForm()
}

async function destroy(type: TicketType, section: Section) {
  try {
    const confirm = type.id
      ? await dialog.open({
        title: 'Confirmação de exclusão',
        message: 'Realmente deseja excluir o tipo de ingresso? Esta ação é irreversível',
      })
      : true

    if (!confirm) return

    if (type.id) {
      loader.increment()
      await ticketService.destroy(model.value.id, type)
      snackbar.show('Tipo de ingresso excluído com sucesso', 'success')
    }

    section.ticket_types = section.ticket_types?.filter(t => t !== type)
  } finally {
    loader.decrement()
  }
}

const sectionOnSorting = ref<Section>()

onMounted(() => {
  sections.value.forEach((s) => {
    s.ticket_types = s.ticket_types?.sort((a, b) => a.sorting_order - b.sorting_order)
  })
})

</script>
